import { apiGetInspectionsListing } from 'app/api/agency-reports/agency-reports';
import { setDetailsActionDone } from 'app/store/agency-reports';
import { RootState } from 'app/store/rootReducer';
import ErrorsList from 'app/ui_v2/components/errors-list/ErrorsList';
import LinesSkeleton from 'app/ui_v2/components/skeletons/lines-skeleton/LinesSkeleton';
import { parseFormValuesToParams, parseSearchParams } from 'app/ui_v2/form-utils';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import CsvButton from './components/csv-button/CsvButton';
import InspectionsData from './components/inspections-data/InspectionsData';
import InspectionsSearchForm from './components/inspections-search-form/InspectionsSearchForm';
import { FormValues } from './components/inspections-search-form/validation-schema';
import { combineResults } from './config';

/**
 * Backoffice Third-Party inspections listing section
 * @returns JSX.Element
 */
export default function InspectionsListingSection() {
  const { user } = useSelector((state: RootState) => state.auth);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const parsedSearchParams = parseSearchParams(searchParams);

  const queryKey = ['backoffice-list-inspections/get', searchParamsString];

  const { isLoading, isFetching, isError, error, data, isSuccess } = useQuery({
    queryKey,
    queryFn: ({ signal }) => apiGetInspectionsListing({ ...parsedSearchParams }, signal),
  });

  const {
    data: dataQa,
    isError: isErrorQa,
    error: errorQa,
    isLoading: isLoadingQa,
    isFetching: isFetchingQa,
  } = useQuery({
    queryKey: ['qa-reports-for-admin-list-inspections/get', searchParamsString],
    queryFn: ({ signal }) => combineResults(data, signal),
    enabled: isSuccess && data !== undefined,
  });

  const dispatch = useDispatch();
  dispatch(setDetailsActionDone(false));

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Box pt={4} pb={2}>
        <Typography component="h2" variant="h1">
          List of inspection events
        </Typography>
      </Box>
      <Box mb={2}>
        <CsvButton queryParams={parsedSearchParams} />
      </Box>
      <InspectionsSearchForm
        onFormChange={(v) => {
          setSearchParams(parseFormValuesToParams({ ...v, page: '1' }));
        }}
        onFormSubmit={(v) => {
          setSearchParams(parseFormValuesToParams({ ...v, page: '1' }));
        }}
        isLoading={isLoading || isLoadingQa}
        ifReset={0}
        defaults={parsedSearchParams as FormValues}
      />

      <>
        {dataQa && (
          <InspectionsData
            data={dataQa.results}
            isFetching={isFetching || isLoading || isLoadingQa || isFetchingQa}
            page={parsedSearchParams?.page || '1'}
            totalPages={dataQa.totalPages}
            onPageChange={(page: string) => {
              setTimeout(() => {
                setSearchParams((currentValues) => {
                  return { ...parseSearchParams(currentValues), page };
                });
              }, 300);
            }}
          />
        )}

        {(isLoading || isLoadingQa) && !isError && !isErrorQa && (
          <Card>
            <CardContent>
              <Box mb={4}>
                <LinesSkeleton elements={4} isWithButtons={false} lineHeight={35} />
              </Box>
            </CardContent>
          </Card>
        )}

        {isError && error && <ErrorsList title="Unexpected loading error" errors={error as string[]} />}
        {isErrorQa && errorQa && (
          <ErrorsList title="Unexpected loading error while QA reports fetching" errors={errorQa as string[]} />
        )}
      </>
    </Container>
  );
}
